import { DataSource, DataSourceLayer, DataSourceVectorAdmin } from '../core/data-sources';

const jsonToText = (value: any): string =>
  value === null || value === undefined ? '' : JSON.stringify(value, null, 2);

const listToText = (value: any[] | null | undefined): string =>
  Array.isArray(value) ? value.join(', ') : '';

const splitList = (value: any): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  return String(value).split(',').map((v) => v.trim()).filter(Boolean);
};

const parseJson = (value: any, label: string, errors: string[]) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch {
    errors.push(`${label} is not valid JSON.`);
    return null;
  }
};

const parseNumberList = (value: any, label: string, errors: string[]): number[] | null => {
  const parts = splitList(value);
  if (parts.length === 0) return null;
  const numbers = parts.map(Number);
  if (numbers.some((n) => Number.isNaN(n))) {
    errors.push(`${label} must be a comma-separated list of numbers.`);
    return null;
  }
  return numbers;
};

const parseDateList = (value: any, label: string, errors: string[]): string[] | null => {
  const parts = splitList(value);
  if (parts.length === 0) return null;
  const invalid = parts.filter((p) => Number.isNaN(new Date(p).getTime()));
  if (invalid.length > 0) {
    errors.push(`${label} contains a date that can't be parsed: ${invalid.join(', ')}.`);
    return null;
  }
  return parts;
};

const emptyToNull = (value: any) => (value === '' || value === undefined ? null : value);

// ShapeType has no single-value select, so a field rendered as 'multi-select'
// hands back an array where the column holds one value. Tolerate both.
const firstOf = (value: any) => {
  if (Array.isArray(value)) return value.length > 0 ? value[0] : null;
  return emptyToNull(value);
};

/** Row (or null, for a new record) -> flat form values. */
export const toFormValues = (row: DataSource | null): Record<string, any> => {
  if (!row) {
    return {
      provider: '',
      model: '',
      geography: '',
      version: '',
      full_name: '',
      description: '',
      export_description: '',
      attribution: '',
      source: 'azure',
      source_path: '',
      access_token_name: '',
      table_name: '',
      format: '',
      filename_prefix: '',
      encrypted_filenames: true,
      regular: false,
      tokens_cost_per_year: null,
      requires_login: true,
      is_private: false,
      has_averages: false,
      deleted: false,
      tiles_domain: '',
      vector_tiles: '',
      start_date: null,
      end_date: null,
      start_date_parts: '',
      end_date_parts: '',
      resolution: '',
      frequency: null,
      grid_resolution: '',
      vertical_reference: '',
      temporal_reference: '',
      owner_organisation_id: [],
      config: '',
      options: '',
    };
  }

  return {
    ...row,
    start_date_parts: listToText(row.start_date_parts),
    end_date_parts: listToText(row.end_date_parts),
    grid_resolution: listToText(row.grid_resolution),
    // A multi-select compares against its MenuItem values, which the renderer
    // stringifies — so this has to be an array of strings, not "38, 4, 40".
    owner_organisation_id: (row.owner_organisation_id || []).map(String),
    config: jsonToText(row.config),
    options: jsonToText(row.options),
  };
};

/** Flat form values -> API payload, with validation. */
export const fromFormValues = (
  vals: Record<string, any>,
): { payload: Record<string, any>; errors: string[] } => {
  const errors: string[] = [];

  const payload: Record<string, any> = {
    provider: emptyToNull(vals.provider),
    model: emptyToNull(vals.model),
    geography: emptyToNull(vals.geography),
    version: emptyToNull(vals.version),
    full_name: emptyToNull(vals.full_name),
    description: emptyToNull(vals.description),
    export_description: emptyToNull(vals.export_description),
    attribution: emptyToNull(vals.attribution),
    bounds: emptyToNull(vals.bounds),

    source: firstOf(vals.source),
    source_path: emptyToNull(vals.source_path),
    access_token_name: emptyToNull(vals.access_token_name),
    table_name: emptyToNull(vals.table_name),
    format: emptyToNull(vals.format),
    filename_prefix: emptyToNull(vals.filename_prefix),
    encrypted_filenames: Boolean(vals.encrypted_filenames),
    regular: Boolean(vals.regular),

    tokens_cost_per_year:
      vals.tokens_cost_per_year === '' || vals.tokens_cost_per_year === null
        ? null
        : Number(vals.tokens_cost_per_year),
    requires_login: Boolean(vals.requires_login),
    is_private: Boolean(vals.is_private),
    has_averages: Boolean(vals.has_averages),

    tiles_domain: emptyToNull(vals.tiles_domain),
    vector_tiles: emptyToNull(vals.vector_tiles),

    start_date: emptyToNull(vals.start_date),
    end_date: emptyToNull(vals.end_date),
    start_date_parts: parseDateList(vals.start_date_parts, 'Start date parts', errors),
    end_date_parts: parseDateList(vals.end_date_parts, 'End date parts', errors),

    resolution: emptyToNull(vals.resolution),
    frequency: vals.frequency === '' || vals.frequency === null ? null : Number(vals.frequency),
    grid_resolution: parseNumberList(vals.grid_resolution, 'Grid resolution', errors),
    vertical_reference: emptyToNull(vals.vertical_reference),
    temporal_reference: emptyToNull(vals.temporal_reference),

    owner_organisation_id: parseNumberList(vals.owner_organisation_id, 'Owner organisations', errors),

    config: parseJson(vals.config, 'Config', errors),
    options: parseJson(vals.options, 'Options', errors),
  };

  // Mirror of data_sources_regular_needs_prefix. Checking here turns a 500 from
  // the constraint into a message next to the field that caused it.
  if (payload.regular && payload.table_name && !payload.filename_prefix) {
    errors.push('Filename prefix is required when "Regular grid" is on and a table name is set.');
  }
  // if (payload.filename_prefix && /[A-Za-z0-9]$/.test(payload.filename_prefix)) {
  //   errors.push('Filename prefix must include its separator, e.g. "ERA5_" rather than "ERA5".');
  // }
  if (payload.start_date && payload.end_date && new Date(payload.start_date) > new Date(payload.end_date)) {
    errors.push('Start date is after end date.');
  }

  return { payload, errors };
};

export const layerToFormValues = (row: DataSourceLayer | null, nextOrder: number) => {
  if (!row) {
    return {
      title: '', short_name: '', key: '', url: '', data_url: '', units: '',
      colours: '', min_value: null, max_value: null, min_value_label: '',
      max_value_label: '', format: '', show_bands: false, hide: false, order: nextOrder,
    };
  }
  return { ...row, colours: jsonToText(row.colours) };
};

export const layerFromFormValues = (
  vals: Record<string, any>,
): { payload: Record<string, any>; errors: string[] } => {
  const errors: string[] = [];
  const payload = {
    title: emptyToNull(vals.title),
    short_name: emptyToNull(vals.short_name),
    key: emptyToNull(vals.key),
    url: emptyToNull(vals.url),
    data_url: emptyToNull(vals.data_url),
    units: emptyToNull(vals.units),
    colours: parseJson(vals.colours, 'Colours', errors),
    min_value: vals.min_value === '' || vals.min_value === null ? null : Number(vals.min_value),
    max_value: vals.max_value === '' || vals.max_value === null ? null : Number(vals.max_value),
    min_value_label: emptyToNull(vals.min_value_label),
    max_value_label: emptyToNull(vals.max_value_label),
    format: emptyToNull(vals.format),
    show_bands: Boolean(vals.show_bands),
    hide: Boolean(vals.hide),
    order: vals.order === '' || vals.order === null ? null : Number(vals.order),
  };

  if (payload.min_value !== null && payload.max_value !== null && payload.min_value > payload.max_value) {
    errors.push('Minimum value is greater than maximum value.');
  }
  return { payload, errors };
};

export const vectorToFormValues = (row: DataSourceVectorAdmin | null, nextOrder: number) => {
  if (!row) {
    return {
      name: '', key: '', mat_key: '', units: '', description: '',
      directional: false, categorical: false, has_height: false,
      type_id: null, order: nextOrder,
    };
  }
  return { ...row };
};

export const vectorFromFormValues = (
  vals: Record<string, any>,
): { payload: Record<string, any>; errors: string[] } => {
  const errors: string[] = [];
  const payload = {
    name: emptyToNull(vals.name),
    key: emptyToNull(vals.key),
    mat_key: emptyToNull(vals.mat_key),
    units: emptyToNull(vals.units),
    description: emptyToNull(vals.description),
    directional: Boolean(vals.directional),
    categorical: Boolean(vals.categorical),
    has_height: Boolean(vals.has_height),
    type_id: firstOf(vals.type_id) === null ? null : Number(firstOf(vals.type_id)),
    order: vals.order === '' || vals.order === null ? null : Number(vals.order),
  };

  if (!payload.key) errors.push('Key is required — the schema response is built from it.');
  return { payload, errors };
};